import React, { useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Warning, Trash, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const DangerZone = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);

  const openDialog = () => {
    setPassword('');
    setConfirmPhrase('');
    setResult(null);
    setShowDialog(true);
  };

  const closeDialog = () => {
    if (working) return;
    setShowDialog(false);
    setPassword('');
    setConfirmPhrase('');
  };

  const CONFIRM_WORD = 'DELETE';
  const canSubmit = password.length > 0 && confirmPhrase === CONFIRM_WORD && !working;

  const handleWipe = async () => {
    if (!canSubmit) return;
    setWorking(true);
    try {
      const res = await adminAPI.wipeDatabase(password);
      setResult(res.data.deleted);
      toast.success('Database cleaned successfully');
    } catch (err) {
      logger.error('DB wipe failed:', err);
      toast.error(err.response?.data?.detail || 'Cleanup failed');
      setPassword('');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="border-2 border-red-300 bg-red-50 p-6" data-testid="danger-zone">
      <div className="flex items-start gap-3 mb-4">
        <Warning size={28} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-lg font-bold text-red-800" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Danger Zone — Database Cleanup
          </h3>
          <p className="text-sm text-red-700 mt-1">
            Permanently delete every order, download token, cart, favourite and registered user account
            (except the admin). Store plans, testimonials and settings are preserved. This cannot be undone.
          </p>
        </div>
      </div>
      <button
        onClick={openDialog}
        className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700"
        data-testid="open-wipe-dialog-btn"
      >
        <Trash size={18} weight="bold" />
        <span>Delete Orders &amp; Users</span>
      </button>

      {showDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="wipe-confirm-dialog">
          <div className="bg-white w-full max-w-md border-2 border-red-500">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-red-50">
              <div className="flex items-center gap-2">
                <Warning size={20} weight="fill" className="text-red-600" />
                <h2 className="text-lg font-bold text-red-800" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Confirm Database Cleanup
                </h2>
              </div>
              <button
                onClick={closeDialog}
                disabled={working}
                className="p-1 hover:bg-red-100 rounded disabled:opacity-50"
                data-testid="close-wipe-dialog-btn"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {result ? (
                <div data-testid="wipe-result">
                  <p className="text-sm font-bold text-green-700 mb-2">Cleanup complete.</p>
                  <ul className="text-sm text-text_muted space-y-1 font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {Object.entries(result).map(([k, v]) => (
                      <li key={k} className="flex justify-between border-b border-border py-1">
                        <span>{k.replace(/_/g, ' ')}</span>
                        <span className="font-bold text-primary">{v}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={closeDialog}
                    className="mt-4 w-full bg-primary text-white px-4 py-2 rounded font-medium hover:bg-opacity-90"
                    data-testid="wipe-done-btn"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
                    <strong>Irreversible action.</strong> This will delete ALL orders, download tokens,
                    user sessions, carts, favourites and non-admin users. There is no undo.
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-primary mb-1">Your admin password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={working}
                      className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-red-500 focus:outline-none"
                      placeholder="Current admin password"
                      data-testid="wipe-password-input"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-primary mb-1">
                      Type <code className="bg-red-100 text-red-800 px-1 font-bold">{CONFIRM_WORD}</code> to confirm
                    </label>
                    <input
                      type="text"
                      value={confirmPhrase}
                      onChange={(e) => setConfirmPhrase(e.target.value)}
                      disabled={working}
                      className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-red-500 focus:outline-none"
                      placeholder={CONFIRM_WORD}
                      data-testid="wipe-confirm-input"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={closeDialog}
                      disabled={working}
                      className="px-4 py-2 border border-border rounded hover:bg-gray-50 disabled:opacity-50"
                      data-testid="wipe-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleWipe}
                      disabled={!canSubmit}
                      className="px-4 py-2 bg-red-600 text-white rounded font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="wipe-confirm-btn"
                    >
                      {working ? 'Wiping...' : 'Delete Everything'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DangerZone;
